import { strings } from "../languages/localizationStrings";
import React from "react";
import "../css/SelfTest.css";
import {
    calibratePxPerMm,
    generateChart,
    scoreAssessment,
    getQuickTestDefaults,
    computeTrend,
    isMobileDevice,
    CREDIT_CARD_WIDTH_MM,
    CREDIT_CARD_HEIGHT_MM,
    TEST_DISTANCES,
} from "../tools/visualAcuity";
import ProgressTracking from "../tools/progressTracking";
import { trackSelfTestStart, trackSelfTestComplete } from "../tools/analytics";

const STEP_INTRO = 'intro';
const STEP_CALIBRATE = 'calibrate';
const STEP_EYE_SELECT = 'eye_select';
const STEP_TEST = 'test';
const STEP_RESULTS = 'results';
const STEP_HISTORY = 'history';

class SelfTest extends React.Component {
    constructor(props) {
        super(props);

        const savedPxPerMm = localStorage.getItem('eyeTrainer_pxPerMm');
        const quickDefaults = getQuickTestDefaults();

        this.state = {
            step: STEP_INTRO,
            // Calibration
            pxPerMm: savedPxPerMm ? parseFloat(savedPxPerMm) : quickDefaults.pxPerMm,
            calibrationWidthPx: 323,
            isDragging: false,
            isCalibrated: !!savedPxPerMm,
            // Test config
            distanceMm: quickDefaults.distanceMm,
            distanceLabel: quickDefaults.distanceLabel,
            currentEye: 'both',
            isMobile: quickDefaults.isMobile,
            // Test state
            chart: null,
            currentLine: 0,
            responses: [],
            missedConsecutive: 0,
            // Results
            results: null,
            // History
            visionHistory: ProgressTracking.getVisionTests(),
        };

        this.sliderRef = React.createRef();
        this.handleSliderChange = this.handleSliderChange.bind(this);
        this.handleMouseDown = this.handleMouseDown.bind(this);
        this.handleMouseMove = this.handleMouseMove.bind(this);
        this.handleMouseUp = this.handleMouseUp.bind(this);
    }

    // ─── Calibration drag ───

    handleSliderChange(e) {
        this.setState({ calibrationWidthPx: parseInt(e.target.value, 10) });
    }

    handleMouseDown(e) {
        e.preventDefault();
        this.setState({ isDragging: true });
        window.addEventListener('mousemove', this.handleMouseMove);
        window.addEventListener('mouseup', this.handleMouseUp);
        window.addEventListener('touchmove', this.handleMouseMove);
        window.addEventListener('touchend', this.handleMouseUp);
    }

    handleMouseMove(e) {
        if (!this.state.isDragging) return;
        const container = this.sliderRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const clientX = e.touches ? e.touches[0].clientX : e.clientX;
        const newWidth = Math.max(100, Math.min(600, clientX - rect.left));
        this.setState({ calibrationWidthPx: Math.round(newWidth) });
    }

    handleMouseUp() {
        this.setState({ isDragging: false });
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
        window.removeEventListener('touchmove', this.handleMouseMove);
        window.removeEventListener('touchend', this.handleMouseUp);
    }

    componentWillUnmount() {
        window.removeEventListener('mousemove', this.handleMouseMove);
        window.removeEventListener('mouseup', this.handleMouseUp);
        window.removeEventListener('touchmove', this.handleMouseMove);
        window.removeEventListener('touchend', this.handleMouseUp);
    }

    // ─── Actions ───

    confirmCalibration() {
        const pxPerMm = calibratePxPerMm(this.state.calibrationWidthPx);
        localStorage.setItem('eyeTrainer_pxPerMm', pxPerMm.toString());
        this.setState({ pxPerMm, isCalibrated: true, step: STEP_EYE_SELECT });
    }

    quickStart() {
        this.setState({ step: STEP_EYE_SELECT });
    }

    startTest(eye) {
        trackSelfTestStart();
        const chart = generateChart(this.state.pxPerMm, this.state.distanceMm);
        this.setState({
            currentEye: eye,
            chart,
            currentLine: 0,
            responses: [],
            missedConsecutive: 0,
            step: STEP_TEST,
        });
    }

    handleLineResponse(canRead) {
        const { currentLine, responses, missedConsecutive, chart } = this.state;
        const newResponses = [...responses, { lineIndex: currentLine, correct: canRead }];
        const newMissed = canRead ? 0 : missedConsecutive + 1;

        if (newMissed >= 2 || currentLine >= chart.length - 1) {
            this.finishTest(newResponses);
        } else {
            this.setState({
                responses: newResponses,
                missedConsecutive: newMissed,
                currentLine: currentLine + 1,
            });
        }
    }

    finishTest(responses) {
        let lastReadable = -1;
        for (let i = responses.length - 1; i >= 0; i--) {
            if (responses[i].correct) {
                lastReadable = responses[i].lineIndex;
                break;
            }
        }
        if (lastReadable < 0) lastReadable = 0;

        const result = scoreAssessment(lastReadable, 0);
        const fullResult = {
            ...result,
            eye: this.state.currentEye,
            distanceMm: this.state.distanceMm,
            lineIndex: lastReadable,
        };

        ProgressTracking.recordVisionTest(fullResult);
        trackSelfTestComplete(`${fullResult.snellenFt} (${fullResult.currentEye})`);

        this.setState({
            results: fullResult,
            visionHistory: ProgressTracking.getVisionTests(),
            step: STEP_RESULTS,
        });
    }

    // ─── Helpers ───

    getEyeLabel(eye) {
        if (eye === 'left') return strings.selfTestLeftEye;
        if (eye === 'right') return strings.selfTestRightEye;
        return strings.selfTestBothEyes;
    }

    getLatestByEye() {
        const tests = this.state.visionHistory;
        const latest = { left: null, right: null, both: null };
        for (let i = tests.length - 1; i >= 0; i--) {
            const t = tests[i];
            if (!latest[t.eye]) latest[t.eye] = t;
            if (latest.left && latest.right && latest.both) break;
        }
        return latest;
    }

    getTrendForEye(eye) {
        const tests = this.state.visionHistory.filter(t => t.eye === eye);
        return computeTrend(tests);
    }

    getAcuityEmoji(description) {
        const map = {
            excellent: '&#11088;',       // star
            normal: '&#9989;',           // check
            near_normal: '&#128064;',    // eyes
            mild_impairment: '&#128306;', // dim
            moderate: '&#9888;',          // warning
            significant: '&#128308;',     // red circle
        };
        return map[description] || '&#128064;';
    }

    getTrendArrow(trend) {
        if (trend === 'improving') return { arrow: '&#9650;', cls: 'trend-up', label: strings.selfTestTrendImproving };
        if (trend === 'declining') return { arrow: '&#9660;', cls: 'trend-down', label: strings.selfTestTrendDeclining };
        if (trend === 'stable') return { arrow: '&#9644;', cls: 'trend-stable', label: strings.selfTestTrendStable };
        return null;
    }

    // ─── Renders ───

    renderLastResultCard() {
        const latest = this.getLatestByEye();
        const anyResult = latest.right || latest.left || latest.both;
        if (!anyResult) return null;

        const eyes = ['right', 'left', 'both'].filter(e => latest[e]);

        return (
            <div className="st-last-results">
                <h3 className="st-section-title">{strings.selfTestLastResults}</h3>
                <div className="st-last-grid">
                    {eyes.map(eye => {
                        const t = latest[eye];
                        const trend = this.getTrendForEye(eye);
                        const trendInfo = this.getTrendArrow(trend);
                        return (
                            <div key={eye} className="st-last-item">
                                <div className="st-last-eye">{this.getEyeLabel(eye)}</div>
                                <div className="st-last-snellen">{t.snellenFt}</div>
                                <div className={`st-last-badge st-acuity-${t.description}`}>
                                    <span dangerouslySetInnerHTML={{ __html: this.getAcuityEmoji(t.description) }} />
                                </div>
                                {trendInfo && (
                                    <div className={`st-trend ${trendInfo.cls}`}>
                                        <span dangerouslySetInnerHTML={{ __html: trendInfo.arrow }} />
                                        <span className="st-trend-label">{trendInfo.label}</span>
                                    </div>
                                )}
                                <div className="st-last-date">
                                    {new Date(t.date).toLocaleDateString()}
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    }

    renderIntro() {
        const hasHistory = this.state.visionHistory.length > 0;
        return (
            <div className="st-intro">
                <h1 className="pageTitle">{strings.selfTestTitle}</h1>

                {/* Last results at the top for returning users */}
                {this.renderLastResultCard()}

                <div className="st-card">
                    <p className="st-description">{strings.selfTestIntro}</p>

                    <div className="st-actions">
                        <button
                            className="st-btn st-btn-primary st-btn-large"
                            onClick={() => this.quickStart()}
                        >
                            {strings.selfTestQuickStart}
                        </button>
                        <button
                            className="st-btn st-btn-secondary"
                            onClick={() => this.setState({ step: STEP_CALIBRATE })}
                        >
                            {this.state.isCalibrated ? strings.selfTestRecalibrate : strings.selfTestCalibrateFirst}
                        </button>
                        {hasHistory && (
                            <button
                                className="st-btn st-btn-ghost"
                                onClick={() => this.setState({ step: STEP_HISTORY })}
                            >
                                {strings.selfTestViewHistory}
                            </button>
                        )}
                    </div>

                    <div className="st-disclaimer">
                        {strings.selfTestDisclaimerShort}
                    </div>
                </div>
            </div>
        );
    }

    renderCalibration() {
        const { calibrationWidthPx } = this.state;
        const heightPx = calibrationWidthPx * (CREDIT_CARD_HEIGHT_MM / CREDIT_CARD_WIDTH_MM);

        return (
            <div className="st-calibrate">
                <h1 className="pageTitle">{strings.selfTestCalibrationTitle}</h1>
                <div className="st-card">
                    <p className="st-description">{strings.selfTestCalibrationDesc}</p>

                    <div className="st-calibration-area" ref={this.sliderRef}>
                        <div
                            className="st-credit-card"
                            style={{ width: calibrationWidthPx, height: heightPx }}
                            onMouseDown={this.handleMouseDown}
                            onTouchStart={this.handleMouseDown}
                        >
                            <div className="st-card-inner">
                                <div className="st-card-chip"></div>
                                <div className="st-card-label">{strings.selfTestCreditCard}</div>
                                <div className="st-card-size">{CREDIT_CARD_WIDTH_MM} x {CREDIT_CARD_HEIGHT_MM} mm</div>
                            </div>
                            <div className="st-drag-handle">
                                <span>&#x2194;</span>
                            </div>
                        </div>
                    </div>

                    <div className="st-slider-row">
                        <input
                            type="range"
                            min="100"
                            max="600"
                            value={calibrationWidthPx}
                            onChange={this.handleSliderChange}
                            className="st-slider"
                        />
                    </div>

                    <div className="st-distance-pick">
                        <p className="st-description">{strings.selfTestDistanceDesc}</p>
                        <div className="st-distance-options">
                            {TEST_DISTANCES.map(d => (
                                <button
                                    key={d.mm}
                                    className={`st-btn st-btn-option ${d.mm === this.state.distanceMm ? 'selected' : ''}`}
                                    onClick={() => this.setState({ distanceMm: d.mm, distanceLabel: d.label })}
                                >
                                    {d.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="st-actions">
                        <button className="st-btn st-btn-primary" onClick={() => this.confirmCalibration()}>
                            {strings.selfTestConfirmCalibration}
                        </button>
                        <button className="st-btn st-btn-ghost" onClick={() => this.setState({ step: STEP_INTRO })}>
                            {strings.selfTestBack}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    renderEyeSelect() {
        return (
            <div className="st-eye-select">
                <h1 className="pageTitle">{strings.selfTestEyeTitle}</h1>
                <div className="st-card">
                    <p className="st-description">{strings.selfTestEyeDesc}</p>

                    <div className="st-eye-buttons">
                        <button className="st-eye-btn" onClick={() => this.startTest('right')}>
                            <span className="st-eye-icon">&#128065;</span>
                            <span className="st-eye-label">{strings.selfTestRightEye}</span>
                        </button>
                        <button className="st-eye-btn" onClick={() => this.startTest('left')}>
                            <span className="st-eye-icon">&#128065;</span>
                            <span className="st-eye-label">{strings.selfTestLeftEye}</span>
                        </button>
                        <button className="st-eye-btn" onClick={() => this.startTest('both')}>
                            <span className="st-eye-icon">&#128064;</span>
                            <span className="st-eye-label">{strings.selfTestBothEyes}</span>
                        </button>
                    </div>

                    <p className="st-hint">{strings.selfTestEyeHint}</p>

                    <div className="st-distance-info">
                        {strings.selfTestDistanceReminder} <strong>{this.state.distanceLabel}</strong>
                    </div>

                    <div className="st-actions">
                        <button className="st-btn st-btn-ghost" onClick={() => this.setState({ step: STEP_INTRO })}>
                            {strings.selfTestBack}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    renderTest() {
        const { chart, currentLine, currentEye } = this.state;
        if (!chart) return null;

        const line = chart[currentLine];
        const progress = ((currentLine + 1) / chart.length) * 100;
        const eyeLabel = this.getEyeLabel(currentEye);

        return (
            <div className="st-test">
                <div className="st-test-header">
                    <span className="st-test-eye">{eyeLabel}</span>
                    <span className="st-test-progress">{currentLine + 1} / {chart.length}</span>
                </div>

                <div className="st-progress-bar">
                    <div className="st-progress-fill" style={{ width: `${progress}%` }}></div>
                </div>

                <div className="st-optotype-area">
                    <div
                        className="st-optotypes"
                        style={{ fontSize: Math.max(line.heightPx, 2) }}
                    >
                        {line.letters.join('  ')}
                    </div>
                </div>

                <p className="st-question">{strings.selfTestCanYouRead}</p>

                <div className="st-test-buttons">
                    <button
                        className="st-answer-btn st-answer-yes"
                        onClick={() => this.handleLineResponse(true)}
                    >
                        <span className="st-answer-icon">&#10004;</span>
                        <span>{strings.selfTestYes}</span>
                    </button>
                    <button
                        className="st-answer-btn st-answer-no"
                        onClick={() => this.handleLineResponse(false)}
                    >
                        <span className="st-answer-icon">&#10008;</span>
                        <span>{strings.selfTestNo}</span>
                    </button>
                </div>
            </div>
        );
    }

    renderResults() {
        const { results } = this.state;
        if (!results) return null;

        const descriptionKey = `selfTestAcuity_${results.description}`;
        const acuityText = strings[descriptionKey] || results.description;
        const eyeLabel = this.getEyeLabel(results.eye);

        return (
            <div className="st-results">
                <h1 className="pageTitle">{strings.selfTestResultsTitle}</h1>
                <div className="st-card">
                    <div className="st-result-eye-label">{eyeLabel}</div>

                    <div className="st-result-big">
                        <span className="st-result-big-emoji" dangerouslySetInnerHTML={{ __html: this.getAcuityEmoji(results.description) }} />
                        <span className="st-result-big-snellen">{results.snellenFt}</span>
                    </div>

                    <div className={`st-acuity-badge st-acuity-${results.description}`}>
                        {acuityText}
                    </div>

                    <div className="st-result-details">
                        <div className="st-result-detail">
                            <span className="st-detail-value">{results.logMAR.toFixed(2)}</span>
                            <span className="st-detail-label">LogMAR</span>
                        </div>
                        <div className="st-result-detail">
                            <span className="st-detail-value">{results.decimal}</span>
                            <span className="st-detail-label">{strings.selfTestDecimal}</span>
                        </div>
                    </div>

                    <div className="st-disclaimer">
                        {strings.selfTestResultsDisclaimer}
                    </div>

                    <div className="st-actions">
                        <button
                            className="st-btn st-btn-primary"
                            onClick={() => this.setState({ step: STEP_EYE_SELECT })}
                        >
                            {strings.selfTestTestAnotherEye}
                        </button>
                        <button
                            className="st-btn st-btn-secondary"
                            onClick={() => this.setState({ step: STEP_HISTORY })}
                        >
                            {strings.selfTestViewHistory}
                        </button>
                        <button
                            className="st-btn st-btn-ghost"
                            onClick={() => this.setState({ step: STEP_INTRO, visionHistory: ProgressTracking.getVisionTests() })}
                        >
                            {strings.selfTestDone}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    renderHistory() {
        const { visionHistory } = this.state;
        const grouped = { right: [], left: [], both: [] };
        visionHistory.forEach(t => {
            if (grouped[t.eye]) grouped[t.eye].push(t);
        });

        return (
            <div className="st-history">
                <h1 className="pageTitle">{strings.selfTestHistoryTitle}</h1>
                <div className="st-card">
                    {visionHistory.length === 0 ? (
                        <p className="st-empty">{strings.selfTestNoHistory}</p>
                    ) : (
                        Object.entries(grouped).map(([eye, tests]) => {
                            if (tests.length === 0) return null;
                            const eyeLabel = this.getEyeLabel(eye);
                            const trend = computeTrend(tests);
                            const trendInfo = this.getTrendArrow(trend);

                            return (
                                <div key={eye} className="st-history-group">
                                    <div className="st-history-eye-header">
                                        <h3 className="st-history-eye">{eyeLabel}</h3>
                                        {trendInfo && (
                                            <span className={`st-trend ${trendInfo.cls}`}>
                                                <span dangerouslySetInnerHTML={{ __html: trendInfo.arrow }} />
                                                {trendInfo.label}
                                            </span>
                                        )}
                                    </div>
                                    <div className="st-history-table">
                                        <div className="st-history-header">
                                            <span>{strings.selfTestDate}</span>
                                            <span>Snellen</span>
                                            <span>LogMAR</span>
                                        </div>
                                        {[...tests].reverse().slice(0, 10).map((t, i) => (
                                            <div key={i} className="st-history-row">
                                                <span>{new Date(t.date).toLocaleDateString()}</span>
                                                <span className="st-history-snellen">{t.snellenFt}</span>
                                                <span>{t.logMAR.toFixed(2)}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            );
                        })
                    )}

                    <div className="st-actions">
                        <button
                            className="st-btn st-btn-primary"
                            onClick={() => this.setState({ step: STEP_EYE_SELECT })}
                        >
                            {strings.selfTestStartNew}
                        </button>
                        <button
                            className="st-btn st-btn-ghost"
                            onClick={() => this.setState({ step: STEP_INTRO })}
                        >
                            {strings.selfTestBack}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    render() {
        const { step } = this.state;
        return (
            <div className="SelfTest">
                {step === STEP_INTRO && this.renderIntro()}
                {step === STEP_CALIBRATE && this.renderCalibration()}
                {step === STEP_EYE_SELECT && this.renderEyeSelect()}
                {step === STEP_TEST && this.renderTest()}
                {step === STEP_RESULTS && this.renderResults()}
                {step === STEP_HISTORY && this.renderHistory()}
            </div>
        );
    }
}

export default SelfTest;
